import { z } from 'zod/mini';
import { MAX_ROUNDS, latLngSchema } from './game';
import type { LatLng } from './geo';

/**
 * Take turns: each round, one player chooses the place and the others guess it. Everyone picks
 * equally often; the picker scores the average of the others' points that round, so picking
 * never moves them up or down the standings, and a place nobody can find is worth nothing.
 */

/** How many times each player picks; the host chooses. */
export const PICKS_EACH_OPTIONS = [1, 2, 3] as const;
export const DEFAULT_PICKS_EACH = 1;
/** How long the picker has to choose. When time runs out, the last place they looked at is played. */
export const PICK_TIME_MS = 90_000;
/** Places a picker can look at in Street View per turn: each one loads a panorama. */
export const PICK_PREVIEWS = 3;
/** Map searches for Street View (free lookups) a picker can make per turn. */
export const PICK_LOOKUPS = 30;

/** Turns each player actually gets: as many as asked, all equal, within the round limit. */
export function picksEach(wanted: number, players: number): number {
  return Math.max(1, Math.min(wanted, Math.floor(MAX_ROUNDS / Math.max(1, players))));
}

/** Rounds of a take-turns game with `players` picking. */
export function pickRounds(wanted: number, players: number): number {
  return Math.min(MAX_ROUNDS, picksEach(wanted, players) * players);
}

/** The panorama found nearest to where the picker clicked: only they see it before the round. */
export interface PickPreview extends LatLng {
  panoId: string;
  heading: number;
  countryCode: string;
  /** How far the panorama is from the click. */
  distanceM: number;
}

export const pickPreviewSchema = z.object({
  roundIndex: z.int().check(z.minimum(0)),
  point: latLngSchema,
});

/** Locks the picker's choice in: the place they last looked at, or a random one. */
export const pickSchema = z.object({
  roundIndex: z.int().check(z.minimum(0)),
  random: z.boolean(),
});
